/**
 * ProjectRagTable — dense portfolio ledger of non-cancelled projects.
 * TanStack Table over the `.ledger` design-system primitive: name in sans +
 * code in mono, RAG pill, subtle status pill, a slim brand-filled completion
 * track, and mono schedule dates. Rows are clickable — navigate to the
 * project workspace at /project/:id. Pure presentation — no data fetching.
 * (Project edit/archive controls live in the project workspace's own
 * Settings section now, not as a per-row Actions column here.)
 */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router';
import type { ProjectRagEntry, ProjectStatus } from '@/types/domain';
import { RagBadge } from './RagBadge';
import { GREEN_TEXT } from './workspace/crStatusTokens';

const STATUS_LABEL: Record<ProjectStatus, string> = {
  PLANNED: 'Planned',
  ACTIVE: 'Active',
  ON_HOLD: 'On Hold',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

// §2.9 chip system — status pill, borderless full pill on the chip-tint
// formula (full-hue text on a same-hue tint); neutral statuses use the
// --surface-3 neutral-chip pairing instead of a bordered rounded-rect.
function StatusPill({ status }: { status: ProjectStatus }) {
  const active = status === 'ACTIVE';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: 'var(--radius-pill)',
        background: active ? 'var(--health-green-bg)' : 'var(--surface-3)',
        border: 'none',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-2xs)',
        fontWeight: 600,
        // QA R2: GREEN_TEXT (adaptive mix, not raw --health-green) — the raw
        // hue measured 4.38:1 on its own tint over the white card (light,
        // below 4.5:1); GREEN_TEXT clears 5.32:1 light / 7.62:1 dark. Same
        // fix as AccountsPage's StatusPill / SprintsSection's "Active" chip.
        color: active ? GREEN_TEXT : 'var(--text-mid)',
        whiteSpace: 'nowrap',
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function MonoDate({ value }: { value?: string }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-xs)',
        color: value ? 'var(--text-muted)' : 'var(--text-subtle)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value ? value.slice(0, 10) : '—'}
    </span>
  );
}

const col = createColumnHelper<ProjectRagEntry>();

const baseColumns = [
  col.accessor('name', {
    header: 'Project',
    cell: (info) => (
      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{info.getValue()}</span>
    ),
  }),
  col.accessor('accountName', {
    header: 'Account',
    cell: (i) => (
      <span style={{ color: 'var(--text-muted)' }}>{i.getValue() ?? '—'}</span>
    ),
  }),
  col.accessor('health', {
    header: 'Health',
    cell: (i) => <RagBadge rag={i.getValue()} />,
  }),
  col.accessor('status', {
    header: 'Status',
    cell: (i) => <StatusPill status={i.getValue()} />,
  }),
  col.accessor('targetEndDate', {
    header: 'Target',
    cell: (i) => <MonoDate value={i.getValue()} />,
  }),
  col.accessor('forecastEndDate', {
    header: 'Forecast',
    cell: (i) => <MonoDate value={i.getValue()} />,
  }),
  col.accessor('ownerMemberName', {
    header: 'Owner',
    cell: (i) => (
      <span style={{ color: 'var(--text-muted)' }}>{i.getValue() ?? '—'}</span>
    ),
  }),
];

interface Props {
  data: ProjectRagEntry[];
}

export function ProjectRagTable({ data }: Props) {
  const navigate = useNavigate();

  const table = useReactTable({
    data,
    columns: baseColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div
      style={{
        // §1.1/§4.3 — borderless luminance elevation, no stroke/shadow on a
        // static card; mirrors the sibling AccountsTable wrapper exactly so
        // the two stacked ledgers read as the same card material. height/flex
        // (M2 T3) are grid-fill-ready for ProjectRagTableHost (T5) without
        // changing rendered output in the page's normal unconstrained flow.
        border: 'none',
        borderRadius: 'var(--radius-panel)',
        overflow: 'hidden',
        boxShadow: 'none',
        background: 'var(--surface-1)',
        // maxHeight (not height) so the card HUGS its rows in a fixed grid cell
        // — a 1-row table looks like a 1-row table, not a tall half-empty tile —
        // while still capping at the cell and scrolling when the list is long.
        // In normal page flow the parent is auto-height, so this resolves to
        // content height exactly as height:100% did (no visual change there).
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'auto' }}>
        <table className="ledger">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} style={{ textAlign: (h.id === 'targetEndDate' || h.id === 'forecastEndDate') ? 'center' : undefined }}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => (
              <tr
                key={row.id}
                className="row-in pressable"
                style={{ cursor: 'pointer', ['--i' as unknown as string]: Math.min(rowIndex, 14) }}
                onClick={() => navigate(`/project/${row.original.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/project/${row.original.id}`);
                  }
                }}
                tabIndex={0}
                aria-label={`Open project ${row.original.name}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} style={{ textAlign: (cell.column.id === 'targetEndDate' || cell.column.id === 'forecastEndDate') ? 'center' : undefined }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td
                  colSpan={baseColumns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px 16px',
                    color: 'var(--text-subtle)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  No active projects in the portfolio yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
